import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { PermAction, PermOptions, TableWithPermissions } from '@/types/base/permissions'
import { evaluatePermissions, Role } from '@/types/base/authuser'
import Authuser from '@/types/base/authuser'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.roles')

export const getRoleById = async (
	supabase: SupabaseClient<Database>,
	roleId: string
) => {
  const { data: role, error } = await supabase
    .from("roles")
    .select("*")
    .eq("id", roleId)
    .single()

  if (!role) {
		logError(logger, error, 'Failed to fetch Role', { roleId })
    throw new Error(error.message)
  }

  return role
}

export const getOptionalRoleById = async (
	supabase: SupabaseClient<Database>,
	roleId: string
) => {
  const { data: role, error } = await supabase
    .from("roles")
    .select("*")
    .eq("id", roleId)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Role', { roleId })
    throw new Error(error.message)
  }

  return role
}

export const getOptionalRoleByName = async (
	supabase: SupabaseClient<Database>,
	roleName: string
) => {
  const { data: role, error } = await supabase
    .from("roles")
    .select("*")
    .eq("name", roleName)
    .maybeSingle()

  if (error) {
		logError(logger, error, 'Failed to fetch Role', { roleName })
    throw new Error(error.message)
  }

  return role
}

export const getAllRoles = async (supabase: SupabaseClient<Database>) => {
  const { data: roles, error } = await supabase
    .from("roles")
    .select("*")

  if (!roles) {
		logError(logger, error, 'Failed to fetch Roles')
    throw new Error(error.message)
  }

  return roles
}

export const getAllRolesWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'roles'>[]> => {
	const allRoles = await getAllRoles(supabase)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'roles'>[] = []

	for (const role of allRoles) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "roles", context: "ANY", uuid: role.id, ws: workspace})

		result.push({ ...role, permissions: perms })
	}

  return result
}

export const createRole = async (
	supabase: SupabaseClient<Database>,
  role: TablesInsert<"roles">
) => {
  const { data: createdRole, error } = await supabase
    .from("roles")
    .insert([role])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Role', { role: printObject(role) })
    throw new Error(error.message)
  }

	logger.info('Role created successfully', { roleId: createdRole.id })
  return createdRole
}

export const createRoles = async (
	supabase: SupabaseClient<Database>,
  roles: TablesInsert<"roles">[]
) => {
  const { data: createdRoles, error } = await supabase
    .from("roles")
    .insert(roles)
    .select("*")

  if (error) {
		logError(logger, error, 'Failed to create Roles', { roles: printObject(roles) })
    throw new Error(error.message)
  }

	logger.info('Roles created successfully', { roles: printObject(roles) })
  return createdRoles
}

export const updateRole = async (
	supabase: SupabaseClient<Database>,
  roleId: string,
  role: TablesUpdate<"roles">
) => {
  const { data: updatedRole, error } = await supabase
    .from("roles")
    .update(role)
    .eq("id", roleId)
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to update Role', { role: printObject(role) })
    throw new Error(error.message)
  }

  return updatedRole
}

export const deleteRole = async (supabase: SupabaseClient<Database>, roleId: string) => {
  const { error } = await supabase.from("roles").delete().eq("id", roleId)

  if (error) {
		logError(logger, error, 'Failed to delete Role', { roleId })
    throw new Error(error.message)
  }

  return true
}

//Roles can be viewed by users and above. Workspaces are not applicable.
//All other operations are reserved for root
export const roleCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	switch (options.action) {
		case PermAction.view:
			switch (options.context) {
				case "Workspaces":
					return false
				default:
					return authuser.isAuthorizedForRole(Role.user)
			}
		case PermAction.edit:
			switch (options.context) {
				case "ANY":  //for switcher
					return authuser?.isAuthorizedForRole(Role.admin)
				default:
					return false
			}
			break;

		default:
			return false
	}
}